/**
 * Iter 233 — Système d'affiliation long terme.
 * Route : /admin/marketing/affiliation
 *
 * 4 onglets :
 *  - Dashboard : KPIs (leads attribués, commissions à venir, gagnées, payées)
 *  - Liens d'affiliation : créer/copier liens tracés (3 types de partenaires)
 *  - Partenaires externes : CRUD apporteurs d'affaires sans compte
 *  - Commissions : liste filtrable + mark-paid
 *
 * Workflow :
 *  1. Crée un partenaire externe (ou utilise un tenant/user interne)
 *  2. Crée un lien tracé pointant vers un formulaire ou URL
 *  3. Le partenaire partage le lien. Au submit du form, le prospect est attribué.
 *  4. Au paiement réel (Stripe payment_succeeded ou opportunity.won), commission générée.
 *  5. Admin marque la commission comme payée.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, Users, Link as LinkIcon, Wallet, BarChart3, Plus, Trash2, Copy, ExternalLink, Check, Edit2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";

const TABS = [
  { id: "dashboard", label: "Tableau de bord", icon: BarChart3 },
  { id: "links", label: "Liens d'affiliation", icon: LinkIcon },
  { id: "partners", label: "Partenaires externes", icon: Users },
  { id: "commissions", label: "Commissions", icon: Wallet },
];

export default function AffiliatePage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [tab, setTab] = useState("dashboard");

  return (
    <div className="space-y-6 p-6 max-w-[1400px] mx-auto" data-testid="affiliate-page">
      <PageHeader
        title="Affiliation"
        subtitle="Apporteurs d'affaires · Liens tracés · Commissions à la signature (et non à l'inscription)"
      />

      <div className="flex gap-2 border-b border-white/5">
        {TABS.map((t) => {
          const Ic = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                active
                  ? "border-amber-400 text-amber-400"
                  : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`affiliate-tab-${t.id}`}
            >
              <Ic className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && <Dashboard api={api} />}
      {tab === "links" && <LinksTab api={api} />}
      {tab === "partners" && <PartnersTab api={api} />}
      {tab === "commissions" && <CommissionsTab api={api} />}
    </div>
  );
}

// ─── Dashboard ─────────────────────────────────────────


function Dashboard({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/affiliate/dashboard").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  if (!data) return <p className="text-slate-400">Aucune donnée</p>;

  const s = data.summary || {};
  const kpis = [
    { label: "Leads attribués", value: s.leads_attributed || 0, color: "text-violet-400", testid: "kpi-leads-attributed" },
    { label: "En attente de signature", value: s.leads_pending_signature || 0, color: "text-amber-400", testid: "kpi-leads-pending" },
    { label: "Liens actifs", value: s.active_links || 0, color: "text-sky-400", testid: "kpi-active-links" },
    { label: "À verser (€)", value: (s.earned_eur || 0).toFixed(2), color: "text-rose-400", testid: "kpi-earned" },
    { label: "Versées (€)", value: (s.paid_eur || 0).toFixed(2), color: "text-emerald-400", testid: "kpi-paid" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="bg-[#14101c] rounded-xl border border-white/5 p-5" data-testid={k.testid}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{k.label}</div>
            <div className={`text-3xl font-bold ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#14101c] rounded-xl border border-white/5 p-6">
        <h3 className="text-base font-semibold text-slate-100 mb-4">Top 5 partenaires</h3>
        {(data.top_partners || []).length === 0 ? (
          <p className="text-sm text-slate-500">Aucune commission enregistrée pour le moment.</p>
        ) : (
          <div className="space-y-2">
            {data.top_partners.map((p) => (
              <div key={p.partner_id} className="flex items-center justify-between p-3 bg-[#0b0811] rounded-lg" data-testid={`top-partner-${p.partner_id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-100">{p.partner_name}</div>
                  <div className="text-xs text-slate-500">{p.partner_type} · {p.count} commission(s)</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono text-amber-400">{(p.earned_eur || 0).toFixed(2)} € à verser</div>
                  <div className="text-xs text-emerald-400">{(p.paid_eur || 0).toFixed(2)} € versées</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-200">
        <strong>💡 Comment ça marche :</strong> Un partenaire partage un lien d'affiliation. Au submit du formulaire, le prospect est <em>verrouillé</em> sur ce partenaire. La commission est générée <em>uniquement</em> au moment du paiement réel (Stripe) ou du passage en stage <code>gagne</code> dans le CRM, même 6 mois plus tard.
      </div>
    </div>
  );
}


// ─── Liens d'affiliation ─────────────────────────────────


function LinksTab({ api }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/affiliate/links");
      setLinks(r.data.links || []);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const onDelete = async (linkId) => {
    if (!window.confirm("Supprimer ce lien ? Les attributions existantes ne seront pas perdues.")) return;
    await api.delete(`/affiliate/links/${linkId}`);
    toast.success("Lien supprimé");
    reload();
  };

  const onCopy = (linkId) => {
    const url = `${window.location.origin}/api/affiliate/r/${linkId}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien copié");
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold text-slate-100">Liens tracés ({links.length})</h3>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-lg text-sm font-semibold"
          data-testid="affiliate-link-create-btn"
        >
          <Plus className="w-4 h-4" /> Nouveau lien
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
      ) : links.length === 0 ? (
        <p className="text-sm text-slate-500 p-8 text-center bg-[#14101c] rounded-xl border border-white/5">Aucun lien d'affiliation. Créez-en un pour commencer.</p>
      ) : (
        <div className="space-y-2">
          {links.map((l) => (
            <div key={l.link_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center justify-between" data-testid={`affiliate-link-${l.link_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-slate-100 truncate">{l.name}</span>
                  <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300">{l.partner_type}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">{l.commission_pct}%</span>
                </div>
                <div className="text-xs text-slate-500 truncate">→ {l.partner_name} · {l.click_count || 0} clics · {l.submission_count || 0} leads · {(l.earned_total_eur || 0).toFixed(2)}€ générés</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => onCopy(l.link_id)} className="p-2 hover:bg-white/5 rounded-lg text-slate-300" data-testid={`affiliate-link-copy-${l.link_id}`}>
                  <Copy className="w-4 h-4" />
                </button>
                <a
                  href={`${window.location.origin}/api/affiliate/r/${l.link_id}`}
                  target="_blank" rel="noreferrer"
                  className="p-2 hover:bg-white/5 rounded-lg text-slate-300"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button onClick={() => onDelete(l.link_id)} className="p-2 hover:bg-rose-500/10 rounded-lg text-rose-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <CreateLinkModal api={api} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); reload(); }} />}
    </div>
  );
}


function CreateLinkModal({ api, onClose, onCreated }) {
  const [forms, setForms] = useState([]);
  const [eligible, setEligible] = useState({ tenants: [], internal_users: [], externals: [] });
  const [name, setName] = useState("");
  const [partnerType, setPartnerType] = useState("external");
  const [partnerId, setPartnerId] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [pct, setPct] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/forms").then((r) => setForms(r.data.forms || []));
    api.get("/affiliate/eligible-partners").then((r) => setEligible(r.data));
  }, [api]);

  const partnerList = partnerType === "tenant" ? eligible.tenants : partnerType === "internal_user" ? eligible.internal_users : eligible.externals;
  const selectedPartner = partnerList.find((p) => p.id === partnerId);

  const submit = async () => {
    if (!name || !partnerId || (!formSlug && !redirectUrl)) {
      toast.error("Tous les champs requis");
      return;
    }
    setSaving(true);
    try {
      const body = { name, partner_type: partnerType, partner_id: partnerId };
      if (formSlug) body.form_slug = formSlug;
      if (redirectUrl) body.redirect_url = redirectUrl;
      if (pct !== "") body.commission_pct = parseFloat(pct);
      await api.post("/affiliate/links", body);
      toast.success("Lien créé");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-[#14101c] border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4" onClick={(e) => e.stopPropagation()} data-testid="affiliate-link-modal">
        <h3 className="text-lg font-semibold text-slate-100">Nouveau lien d'affiliation</h3>

        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Nom du lien</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="ex: Lien OF Beta - Promo Q1"
            className="w-full px-3 py-2 bg-[#0b0811] border border-white/10 rounded-lg text-sm text-slate-100"
            data-testid="affiliate-link-name" />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Type de partenaire</label>
          <div className="flex gap-2">
            {[{ id: "external", label: "Externe" }, { id: "tenant", label: "Autre OF" }, { id: "internal_user", label: "Commercial interne" }].map((t) => (
              <button key={t.id}
                onClick={() => { setPartnerType(t.id); setPartnerId(""); }}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border ${
                  partnerType === t.id ? "bg-amber-400 text-slate-900 border-amber-400" : "bg-[#0b0811] border-white/10 text-slate-300"
                }`}
                data-testid={`affiliate-partner-type-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Partenaire</label>
          <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}
            className="w-full px-3 py-2 bg-[#0b0811] border border-white/10 rounded-lg text-sm text-slate-100"
            data-testid="affiliate-partner-select"
          >
            <option value="">— Sélectionner —</option>
            {partnerList.map((p) => (
              <option key={p.id} value={p.id}>{p.name} {p.default_pct ? `(défaut ${p.default_pct}%)` : ""}</option>
            ))}
          </select>
          {partnerType === "external" && partnerList.length === 0 && (
            <p className="text-xs text-slate-500 mt-1">Aucun partenaire externe. Allez dans l'onglet "Partenaires externes" pour en créer un.</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">% de commission (override partenaire)</label>
          <input type="number" min="0" max="100" step="0.5"
            value={pct} onChange={(e) => setPct(e.target.value)}
            placeholder={`Défaut : ${selectedPartner?.default_pct || 0}%`}
            className="w-full px-3 py-2 bg-[#0b0811] border border-white/10 rounded-lg text-sm text-slate-100"
            data-testid="affiliate-pct-input" />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Destination</label>
          <select value={formSlug} onChange={(e) => { setFormSlug(e.target.value); setRedirectUrl(""); }}
            className="w-full px-3 py-2 bg-[#0b0811] border border-white/10 rounded-lg text-sm text-slate-100 mb-2"
            data-testid="affiliate-form-select"
          >
            <option value="">— Aucun formulaire (URL externe) —</option>
            {forms.map((f) => <option key={f.form_id} value={f.slug}>{f.name}</option>)}
          </select>
          {!formSlug && (
            <input value={redirectUrl} onChange={(e) => setRedirectUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 bg-[#0b0811] border border-white/10 rounded-lg text-sm text-slate-100" />
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Annuler</button>
          <button onClick={submit} disabled={saving}
            className="px-5 py-2 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-lg text-sm font-semibold disabled:opacity-50"
            data-testid="affiliate-link-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer le lien"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Partenaires externes ─────────────────────────────────


function PartnersTab({ api }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit existing

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/affiliate/partners");
      setPartners(r.data.partners || []);
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce partenaire ?")) return;
    await api.delete(`/affiliate/partners/${id}`);
    toast.success("Partenaire supprimé");
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold text-slate-100">Partenaires externes ({partners.length})</h3>
        <button onClick={() => setEditing({})}
          className="flex items-center gap-2 px-4 py-2 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-lg text-sm font-semibold"
          data-testid="affiliate-partner-create-btn">
          <Plus className="w-4 h-4" /> Nouveau partenaire
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
      ) : partners.length === 0 ? (
        <p className="text-sm text-slate-500 p-8 text-center bg-[#14101c] rounded-xl border border-white/5">Aucun apporteur d'affaires externe enregistré.</p>
      ) : (
        <div className="space-y-2">
          {partners.map((p) => (
            <div key={p.partner_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center justify-between" data-testid={`affiliate-partner-${p.partner_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-slate-100">{p.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">{p.default_commission_pct}% par défaut</span>
                  {!p.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-400">Inactif</span>}
                </div>
                <div className="text-xs text-slate-500">{p.email || "—"} · {p.phone || "—"} {p.iban && `· IBAN ${p.iban.slice(0, 12)}…`}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(p)} className="p-2 hover:bg-white/5 rounded-lg text-slate-300">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => onDelete(p.partner_id)} className="p-2 hover:bg-rose-500/10 rounded-lg text-rose-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && <PartnerEditModal api={api} partner={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}


function PartnerEditModal({ api, partner, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: partner.name || "",
    email: partner.email || "",
    phone: partner.phone || "",
    company: partner.company || "",
    iban: partner.iban || "",
    default_commission_pct: partner.default_commission_pct || 10,
    notes: partner.notes || "",
    active: partner.active !== false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      const body = { ...form };
      if (partner.partner_id) body.partner_id = partner.partner_id;
      await api.post("/affiliate/partners", body);
      toast.success(partner.partner_id ? "Partenaire mis à jour" : "Partenaire créé");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-[#14101c] border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-3" onClick={(e) => e.stopPropagation()} data-testid="affiliate-partner-modal">
        <h3 className="text-lg font-semibold text-slate-100">{partner.partner_id ? "Modifier" : "Nouveau"} partenaire</h3>

        {[
          { k: "name", label: "Nom *" },
          { k: "company", label: "Société" },
          { k: "email", label: "Email" },
          { k: "phone", label: "Téléphone" },
          { k: "iban", label: "IBAN (pour virement commission)" },
        ].map((f) => (
          <div key={f.k}>
            <label className="block text-xs font-medium text-slate-400 mb-1">{f.label}</label>
            <input value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
              className="w-full px-3 py-2 bg-[#0b0811] border border-white/10 rounded-lg text-sm text-slate-100"
              data-testid={`affiliate-partner-field-${f.k}`} />
          </div>
        ))}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">% de commission par défaut</label>
          <input type="number" min="0" max="100" step="0.5"
            value={form.default_commission_pct}
            onChange={(e) => setForm({ ...form, default_commission_pct: e.target.value })}
            className="w-full px-3 py-2 bg-[#0b0811] border border-white/10 rounded-lg text-sm text-slate-100"
            data-testid="affiliate-partner-pct" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Notes</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full px-3 py-2 bg-[#0b0811] border border-white/10 rounded-lg text-sm text-slate-100" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Actif
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Annuler</button>
          <button onClick={submit} disabled={saving}
            className="px-5 py-2 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-lg text-sm font-semibold disabled:opacity-50"
            data-testid="affiliate-partner-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Commissions ─────────────────────────────────────────


function CommissionsTab({ api }) {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [totals, setTotals] = useState({ earned_eur: 0, paid_eur: 0, count: 0 });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/affiliate/commissions${filter ? `?status=${filter}` : ""}`);
      setCommissions(r.data.commissions || []);
      setTotals(r.data.totals || {});
    } finally { setLoading(false); }
  }, [api, filter]);

  useEffect(() => { reload(); }, [reload]);

  const onMarkPaid = async (id) => {
    const ref = window.prompt("Référence du paiement (optionnel) :", "");
    if (ref === null) return;
    try {
      await api.post(`/affiliate/commissions/${id}/mark-paid`, { payment_ref: ref });
      toast.success("Commission marquée payée");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex gap-2">
          {[{ id: "", label: "Toutes" }, { id: "earned", label: "À verser" }, { id: "paid", label: "Versées" }].map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                filter === f.id ? "bg-amber-400 text-slate-900" : "bg-[#14101c] text-slate-300 border border-white/10"
              }`}
              data-testid={`commission-filter-${f.id || "all"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-4 text-sm">
          <span className="text-slate-400">À verser : <span className="text-amber-400 font-mono">{(totals.earned_eur || 0).toFixed(2)}€</span></span>
          <span className="text-slate-400">Versées : <span className="text-emerald-400 font-mono">{(totals.paid_eur || 0).toFixed(2)}€</span></span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
      ) : commissions.length === 0 ? (
        <p className="text-sm text-slate-500 p-8 text-center bg-[#14101c] rounded-xl border border-white/5">Aucune commission.</p>
      ) : (
        <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Partenaire</th>
                <th className="px-4 py-3">Prospect</th>
                <th className="px-4 py-3">Deal</th>
                <th className="px-4 py-3">Commission</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.commission_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`commission-row-${c.commission_id}`}>
                  <td className="px-4 py-3 text-slate-400 text-xs">{(c.earned_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-slate-100">{c.partner_name} <span className="text-xs text-slate-500">({c.partner_type})</span></td>
                  <td className="px-4 py-3 text-slate-300 text-xs font-mono">{c.prospect_id}</td>
                  <td className="px-4 py-3 text-slate-300 font-mono">{(c.deal_amount_ht || 0).toFixed(2)}€</td>
                  <td className="px-4 py-3 text-amber-400 font-mono font-semibold">{c.commission_amount_ht.toFixed(2)}€ <span className="text-xs text-slate-500">({c.commission_pct}%)</span></td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.status === "paid" ? "bg-emerald-500/20 text-emerald-300" :
                      c.status === "earned" ? "bg-amber-500/20 text-amber-300" :
                      "bg-slate-700 text-slate-400"
                    }`}>
                      {c.status === "paid" ? "Versée" : c.status === "earned" ? "À verser" : c.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.status === "earned" && (
                      <button onClick={() => onMarkPaid(c.commission_id)}
                        className="text-xs px-3 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-lg flex items-center gap-1"
                        data-testid={`commission-mark-paid-${c.commission_id}`}>
                        <Check className="w-3 h-3" /> Marquer payée
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
